import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AgentLogin.css";
import { getAuth, signInWithEmailAndPassword, setPersistence, browserLocalPersistence, signOut, sendPasswordResetEmail } from "firebase/auth";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";


const auth = getAuth();

const SESSION_EXPIRY_MINUTES = 60;

const AgentLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  useEffect(() => {
    const storedLoginTime = localStorage.getItem("loginTimestamp");

    if (storedLoginTime) {
      const elapsedMinutes = (Date.now() - parseInt(storedLoginTime, 10)) / 60000;

      if (elapsedMinutes > SESSION_EXPIRY_MINUTES) {
        console.log("🔴 Session expired. Logging out...");
        handleLogout();
      }
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(""); // Reset error when user types
  };

  const handleResetEmailChange = (e) => {
    setResetEmail(e.target.value);
    setResetMessage(""); // Clear previous messages
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const agentId = userCredential.user.uid; // Get the agent's unique ID

      console.log("✅ Agent Logged In:", agentId);
      localStorage.setItem("loginTimestamp", Date.now().toString());

      // Redirect to the agent dashboard & pass agentId in the URL
      navigate(`/agent-dashboard?agentId=${agentId}`);
    } catch (error) {
      console.error("❌ Login failed:", error.message);
      setError("Invalid email or password.");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    localStorage.removeItem("loginTimestamp");
    navigate("/agent-login");
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetMessage("");

    if (!resetEmail) {
      setResetMessage("Please enter your email address.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage("Password reset email sent! Check your inbox.");
      // Keep reset form open so user can see the success message
    } catch (error) {
      console.error("❌ Password reset failed:", error.message);
      if (error.code === "auth/user-not-found") {
        setResetMessage("No account found with this email address.");
      } else {
        setResetMessage("Failed to send reset email. Please try again.");
      }
    }
  };

  const toggleResetForm = () => {
    setIsResetting(!isResetting);
    setResetMessage("");
    if (!isResetting) {
      // If opening the reset form, prefill with login email if available
      setResetEmail(formData.email || "");
    }
  };

  return (
    <div className="agent-login-container">
      <div className="login-card">
        <h2 className="login-title">Agent Login</h2>
        <p className="login-subtitle">Sign in to track your registrations</p>

        {!isResetting ? (
          // Login Form
          <form onSubmit={handleSubmit} className="login-form">
            <div className="input-group">
              <input
                type="email"
                id="email"
                name="email"
                placeholder=""
                value={formData.email}
                onChange={handleChange}
                required
              />
              <label htmlFor="email">Email</label>
            </div>

            <div className="input-group">
              <input
                type="password"
                id="password"
                name="password"
                placeholder=""
                value={formData.password}
                onChange={handleChange}
                required
              />
              <label htmlFor="password">Password</label>
            </div>

            {error && <p className="error-message">{error}</p>}

            <button type="submit" className="login-btn">Login</button>
          </form>
        ) : (
          // Password Reset Form
          <form onSubmit={handleResetPassword} className="login-form">
            <p className="reset-instructions">Enter your email address and we'll send you a link to reset your password.</p>

            <div className="input-group">
              <input
                type="email"
                id="reset-email"
                name="reset-email"
                placeholder=""
                value={resetEmail}
                onChange={handleResetEmailChange}
                required
              />
              <label htmlFor="reset-email">Email</label>
            </div>

            {resetMessage && <p className={resetMessage.includes("sent") ? "success-message" : "error-message"}>{resetMessage}</p>}

            <button type="submit" className="login-btn">Send Reset Link</button>
            <button type="button" onClick={toggleResetForm} className="back-btn-icon">
              <FontAwesomeIcon icon={faArrowLeft} /> <span className="back-text">Back</span>
            </button>

          </form>
        )}

        <div className="login-footer">
          {!isResetting ? (
            <>
              <div className="question">Forgot password? <a href="#" onClick={(e) => { e.preventDefault(); toggleResetForm(); }}>Reset it</a></div>
              <div>Not an agent? <a href="/agent-signup">Register</a></div>
            </>
          ) : (
            <div>Remember your password? <a href="#" onClick={(e) => { e.preventDefault(); toggleResetForm(); }}>Sign in</a></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentLogin;